import React, { useState, useEffect, useRef, useMemo } from 'react';
import axios from 'axios';
import { Plus, Edit2, Trash2, Upload, X, Tags, Merge, RefreshCw, AlertTriangle, Search, FolderTree, Check } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyForm = { name: '', slug: '', logo_url: '', description: '', is_active: true, seo_title: '', seo_description: '', links: [] };

// Category/subcategory link picker used inside the brand dialog
const BrandLinksPicker = ({ categories, links, onChange }) => {
    const linkMap = useMemo(() => Object.fromEntries(links.map(l => [l.category_id, l])), [links]);

    const toggleCategory = (catId) => {
        if (linkMap[catId]) onChange(links.filter(l => l.category_id !== catId));
        else onChange([...links, { category_id: catId, subcategories: [] }]);
    };

    const toggleSub = (catId, sub) => {
        onChange(links.map(l => {
            if (l.category_id !== catId) return l;
            const has = (l.subcategories || []).includes(sub);
            return { ...l, subcategories: has ? l.subcategories.filter(s => s !== sub) : [...(l.subcategories || []), sub] };
        }));
    };

    return (
        <div className="space-y-2" data-testid="brand-links-picker">
            <Label className="dark:text-slate-300 flex items-center gap-1.5"><FolderTree className="h-4 w-4" /> Свързани категории и подкатегории</Label>
            <p className="text-xs text-muted-foreground dark:text-slate-400">Изберете къде да се предлага марката. Без избрана подкатегория = важи за цялата категория.</p>
            <div className="border rounded-lg dark:border-slate-600 divide-y dark:divide-slate-600 max-h-72 overflow-y-auto">
                {categories.map(cat => {
                    const link = linkMap[cat.category_id];
                    return (
                        <div key={cat.category_id} className="p-2.5">
                            <button
                                type="button"
                                onClick={() => toggleCategory(cat.category_id)}
                                data-testid={`brand-link-cat-${cat.category_id}`}
                                className={`w-full text-left text-sm font-medium flex items-center gap-2 rounded px-2 py-1.5 transition-colors ${link ? 'bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-300' : 'hover:bg-slate-50 dark:hover:bg-slate-700 dark:text-slate-200'}`}
                            >
                                <span className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${link ? 'bg-amber-500 border-amber-500' : 'border-slate-300 dark:border-slate-500'}`}>
                                    {link && <Check className="h-3 w-3 text-white" />}
                                </span>
                                {cat.name}
                            </button>
                            {link && (cat.subcategories || []).length > 0 && (
                                <div className="mt-1.5 ml-7 flex flex-wrap gap-1.5">
                                    {(cat.subcategories || []).map(sub => {
                                        const on = (link.subcategories || []).includes(sub);
                                        return (
                                            <button
                                                key={sub}
                                                type="button"
                                                onClick={() => toggleSub(cat.category_id, sub)}
                                                data-testid={`brand-link-sub-${cat.category_id}-${sub}`}
                                                className={`text-xs px-2 py-1 rounded-full border transition-colors ${on ? 'bg-amber-500 border-amber-500 text-white' : 'border-slate-300 dark:border-slate-500 dark:text-slate-300 hover:border-amber-400'}`}
                                            >
                                                {sub}
                                            </button>
                                        );
                                    })}
                                    {(link.subcategories || []).length === 0 && (
                                        <span className="text-[11px] text-muted-foreground dark:text-slate-400 self-center">цялата категория</span>
                                    )}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export const BrandsManager = () => {
    const { token } = useAuth();
    const [brands, setBrands] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showDialog, setShowDialog] = useState(false);
    const [editingBrand, setEditingBrand] = useState(null);
    const [formData, setFormData] = useState(emptyForm);
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState(false);
    const fileInputRef = useRef(null);
    const [missingBrand, setMissingBrand] = useState([]);
    // Filters
    const [search, setSearch] = useState('');
    const [filterCat, setFilterCat] = useState('__all__');
    const [filterSub, setFilterSub] = useState('__all__');
    // Merge
    const [showMerge, setShowMerge] = useState(false);
    const [mergeSource, setMergeSource] = useState('');
    const [mergeTarget, setMergeTarget] = useState('');
    // Delete
    const [deleteInfo, setDeleteInfo] = useState(null);
    const [reassignTo, setReassignTo] = useState('');

    const headers = { Authorization: `Bearer ${token}` };

    const fetchBrands = async () => {
        try {
            const [bRes, mRes, cRes] = await Promise.all([
                axios.get(`${API}/admin/brands`, { headers }),
                axios.get(`${API}/admin/products/missing-brand`, { headers }),
                axios.get(`${API}/categories`)
            ]);
            setBrands(bRes.data.brands || []);
            setMissingBrand(mRes.data.products || []);
            setCategories((cRes.data || []).filter(c => c.category_id));
        } catch (e) {
            toast.error('Грешка при зареждане на марките');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchBrands(); }, []); // eslint-disable-line

    const openEdit = (brand) => {
        setEditingBrand(brand);
        setFormData({
            name: brand.name || '', slug: brand.slug || '', logo_url: brand.logo_url || '',
            description: brand.description || '', is_active: brand.is_active !== false,
            seo_title: brand.seo_title || '', seo_description: brand.seo_description || '',
            links: (brand.links || []).map(l => ({ category_id: l.category_id, subcategories: l.subcategories || [] }))
        });
        setShowDialog(true);
    };

    const resetForm = () => { setEditingBrand(null); setFormData(emptyForm); };

    const handleSubmit = async (e) => {
        e?.preventDefault();
        if (!formData.name.trim()) { toast.error('Името е задължително'); return; }
        setSaving(true);
        try {
            if (editingBrand) {
                await axios.put(`${API}/admin/brands/${editingBrand.brand_id}`, formData, { headers });
                toast.success('Марката е обновена');
            } else {
                const res = await axios.post(`${API}/admin/brands`, formData, { headers });
                if (res.data.existing) toast.info(`Марка "${res.data.brand.name}" вече съществува — връзките са добавени към нея`);
                else toast.success('Марката е създадена');
            }
            setShowDialog(false);
            resetForm();
            await fetchBrands();
        } catch (e2) {
            const d = e2.response?.data?.detail;
            toast.error(typeof d === 'string' ? d : 'Грешка при запазване');
        } finally {
            setSaving(false);
        }
    };

    const toggleActive = async (brand) => {
        try {
            await axios.put(`${API}/admin/brands/${brand.brand_id}`, { is_active: brand.is_active === false }, { headers });
            setBrands(prev => prev.map(b => b.brand_id === brand.brand_id ? { ...b, is_active: brand.is_active === false } : b));
        } catch { toast.error('Грешка'); }
    };

    const handleFileUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        if (!file.type.startsWith('image/')) { toast.error('Моля, изберете изображение'); return; }
        if (file.size > 5 * 1024 * 1024) { toast.error('Файлът е твърде голям (макс. 5MB)'); return; }
        setUploading(true);
        const fd = new FormData();
        fd.append('file', file);
        try {
            const res = await axios.post(`${API}/admin/upload-image`, fd, { headers });
            setFormData(prev => ({ ...prev, logo_url: res.data.url }));
            toast.success('Логото е качено');
        } catch { toast.error('Грешка при качване'); } finally { setUploading(false); }
    };

    const tryDelete = async (brand) => {
        try {
            await axios.delete(`${API}/admin/brands/${brand.brand_id}`, { headers });
            toast.success('Марката е изтрита');
            await fetchBrands();
        } catch (e) {
            if (e.response?.status === 409) {
                setDeleteInfo({ brand, ...e.response.data.detail });
                setReassignTo('');
            } else {
                toast.error(e.response?.data?.detail || 'Грешка при изтриване');
            }
        }
    };

    const confirmDelete = async (mode) => {
        const params = mode === 'reassign' ? { reassign_to: reassignTo } : { force: true };
        try {
            await axios.delete(`${API}/admin/brands/${deleteInfo.brand.brand_id}`, { headers, params });
            toast.success('Марката е изтрита');
            setDeleteInfo(null);
            await fetchBrands();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Грешка при изтриване');
        }
    };

    const handleMerge = async () => {
        if (!mergeSource || !mergeTarget || mergeSource === mergeTarget) { toast.error('Изберете две различни марки'); return; }
        try {
            const res = await axios.post(`${API}/admin/brands/merge`, { source_id: mergeSource, target_id: mergeTarget }, { headers });
            toast.success(res.data.message);
            setShowMerge(false); setMergeSource(''); setMergeTarget('');
            await fetchBrands();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Грешка при обединяване');
        }
    };

    const filterCatObj = categories.find(c => c.category_id === filterCat);

    const visibleBrands = brands.filter(b => {
        if (search && !(b.name || '').toLowerCase().includes(search.toLowerCase())) return false;
        if (filterCat !== '__all__') {
            const link = (b.links || []).find(l => l.category_id === filterCat);
            if (!link) return false;
            if (filterSub !== '__all__') {
                const subs = link.subcategories || [];
                if (subs.length > 0 && !subs.includes(filterSub)) return false;
            }
        }
        return true;
    });

    if (loading) return <div className="p-8 text-center dark:text-slate-300">Зареждане...</div>;

    return (
        <div>
            <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
                <div className="flex items-center gap-3">
                    <h1 className="font-serif text-3xl font-bold dark:text-white flex items-center gap-2"><Tags className="h-7 w-7 text-amber-500" /> Марки</h1>
                    <Button variant="outline" size="icon" onClick={fetchBrands} className="dark:border-slate-600" title="Обнови">
                        <RefreshCw className="h-4 w-4 dark:text-slate-300" />
                    </Button>
                </div>
                <div className="flex gap-2">
                    <Button variant="outline" onClick={() => setShowMerge(true)} className="dark:border-slate-600 dark:text-white" data-testid="merge-brands-btn">
                        <Merge className="h-4 w-4 mr-2" /> Обедини марки
                    </Button>
                    <Dialog open={showDialog} onOpenChange={(open) => { setShowDialog(open); if (!open) resetForm(); }}>
                        <DialogTrigger asChild>
                            <Button className="btn-primary" data-testid="add-brand-btn"><Plus className="h-4 w-4 mr-2" /> Добави марка</Button>
                        </DialogTrigger>
                        <DialogContent className="dark:bg-slate-800 dark:border-slate-700 max-h-[90vh] overflow-y-auto sm:max-w-lg">
                            <DialogHeader>
                                <DialogTitle className="dark:text-white">{editingBrand ? 'Редактирай марка' : 'Нова марка'}</DialogTitle>
                            </DialogHeader>
                            <form onSubmit={handleSubmit} className="space-y-4">
                                <div>
                                    <Label className="dark:text-slate-300">Име *</Label>
                                    <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required data-testid="brand-name-input" className="dark:bg-slate-700 dark:border-slate-600" />
                                </div>
                                <BrandLinksPicker
                                    categories={categories}
                                    links={formData.links}
                                    onChange={(links) => setFormData({ ...formData, links })}
                                />
                                <div>
                                    <Label className="dark:text-slate-300">Slug</Label>
                                    <Input value={formData.slug} onChange={(e) => setFormData({ ...formData, slug: e.target.value })} placeholder="Генерира се автоматично" className="dark:bg-slate-700 dark:border-slate-600" />
                                </div>
                                <div>
                                    <Label className="dark:text-slate-300">Лого / изображение</Label>
                                    <div className="mt-2 flex items-center gap-3">
                                        {formData.logo_url && (
                                            <div className="relative w-16 h-16 rounded-lg overflow-hidden border dark:border-slate-600">
                                                <img src={formData.logo_url} alt="Лого" className="w-full h-full object-cover" />
                                                <button type="button" onClick={() => setFormData({ ...formData, logo_url: '' })} className="absolute top-0 right-0 p-0.5 bg-red-500 text-white rounded-bl"><X className="h-3 w-3" /></button>
                                            </div>
                                        )}
                                        <input type="file" ref={fileInputRef} onChange={handleFileUpload} accept="image/*" className="hidden" />
                                        <Button type="button" variant="outline" onClick={() => fileInputRef.current?.click()} disabled={uploading} className="dark:border-slate-600 dark:text-white">
                                            <Upload className="h-4 w-4 mr-2" /> {uploading ? 'Качване...' : (formData.logo_url ? 'Смени' : 'Качи лого')}
                                        </Button>
                                    </div>
                                </div>
                                <div>
                                    <Label className="dark:text-slate-300">Кратко описание</Label>
                                    <Textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} rows={2} className="dark:bg-slate-700 dark:border-slate-600" />
                                </div>
                                <div>
                                    <Label className="dark:text-slate-300">SEO заглавие</Label>
                                    <Input value={formData.seo_title} onChange={(e) => setFormData({ ...formData, seo_title: e.target.value })} className="dark:bg-slate-700 dark:border-slate-600" />
                                </div>
                                <div>
                                    <Label className="dark:text-slate-300">SEO описание</Label>
                                    <Textarea value={formData.seo_description} onChange={(e) => setFormData({ ...formData, seo_description: e.target.value })} rows={2} className="dark:bg-slate-700 dark:border-slate-600" />
                                </div>
                                <div className="flex items-center justify-between">
                                    <Label className="dark:text-slate-300">Активна</Label>
                                    <Switch checked={formData.is_active} onCheckedChange={(v) => setFormData({ ...formData, is_active: v })} />
                                </div>
                                <Button type="submit" disabled={saving} className="btn-primary w-full" data-testid="save-brand-btn">
                                    {saving ? 'Запазване...' : (editingBrand ? 'Запази промените' : 'Създай марка')}
                                </Button>
                            </form>
                        </DialogContent>
                    </Dialog>
                </div>
            </div>

            {/* Search & filters */}
            <div className="flex flex-wrap gap-2 mb-4">
                <div className="relative flex-1 min-w-[180px] max-w-xs">
                    <Search className="h-4 w-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Търси марка..." className="pl-8 dark:bg-slate-700 dark:border-slate-600" data-testid="brand-search-input" />
                </div>
                <Select value={filterCat} onValueChange={(v) => { setFilterCat(v); setFilterSub('__all__'); }}>
                    <SelectTrigger className="w-[200px] dark:bg-slate-700 dark:border-slate-600" data-testid="brand-filter-category">
                        <SelectValue placeholder="Всички категории" />
                    </SelectTrigger>
                    <SelectContent className="dark:bg-slate-800">
                        <SelectItem value="__all__">Всички категории</SelectItem>
                        {categories.map(c => <SelectItem key={c.category_id} value={c.category_id}>{c.name}</SelectItem>)}
                    </SelectContent>
                </Select>
                {filterCat !== '__all__' && (filterCatObj?.subcategories || []).length > 0 && (
                    <Select value={filterSub} onValueChange={setFilterSub}>
                        <SelectTrigger className="w-[200px] dark:bg-slate-700 dark:border-slate-600" data-testid="brand-filter-subcategory">
                            <SelectValue placeholder="Всички подкатегории" />
                        </SelectTrigger>
                        <SelectContent className="dark:bg-slate-800">
                            <SelectItem value="__all__">Всички подкатегории</SelectItem>
                            {(filterCatObj?.subcategories || []).map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                        </SelectContent>
                    </Select>
                )}
                {(search || filterCat !== '__all__') && (
                    <Button variant="ghost" size="sm" onClick={() => { setSearch(''); setFilterCat('__all__'); setFilterSub('__all__'); }} className="dark:text-slate-300" data-testid="brand-clear-filters-btn">
                        <X className="h-4 w-4 mr-1" /> Изчисти
                    </Button>
                )}
            </div>

            {missingBrand.length > 0 && (
                <div className="mb-6 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-300 dark:border-amber-700 rounded-xl" data-testid="missing-brand-alert">
                    <p className="font-medium text-amber-800 dark:text-amber-400 flex items-center gap-2">
                        <AlertTriangle className="h-4 w-4" /> Липсваща марка: {missingBrand.length} продукта в категории, изискващи марка
                    </p>
                    <div className="mt-2 flex flex-wrap gap-2">
                        {missingBrand.slice(0, 10).map(p => (
                            <Badge key={p.product_id} variant="outline" className="border-amber-400 text-amber-700 dark:text-amber-400">
                                {p.name || p.product_id} {p.category_name ? `(${p.category_name})` : ''}
                            </Badge>
                        ))}
                        {missingBrand.length > 10 && <Badge variant="outline">+{missingBrand.length - 10} още</Badge>}
                    </div>
                </div>
            )}

            <div className="bg-white dark:bg-slate-800 rounded-xl border border-border dark:border-slate-700 overflow-x-auto">
                <table className="w-full min-w-[640px]" data-testid="brands-table">
                    <thead className="bg-slate-50 dark:bg-slate-700/50">
                        <tr className="text-left text-sm">
                            <th className="p-4 dark:text-slate-200">Марка</th>
                            <th className="p-4 dark:text-slate-200">Категории → Подкатегории</th>
                            <th className="p-4 dark:text-slate-200">Продукти</th>
                            <th className="p-4 dark:text-slate-200">Активна</th>
                            <th className="p-4 dark:text-slate-200 text-right">Действия</th>
                        </tr>
                    </thead>
                    <tbody>
                        {visibleBrands.length === 0 && (
                            <tr><td colSpan={5} className="p-8 text-center text-muted-foreground dark:text-slate-400">
                                {brands.length === 0 ? 'Няма създадени марки. Добавете първата си марка.' : 'Няма марки, отговарящи на филтъра.'}
                            </td></tr>
                        )}
                        {visibleBrands.map(b => (
                            <tr key={b.brand_id} className="border-t dark:border-slate-700" data-testid={`brand-row-${b.slug}`}>
                                <td className="p-4">
                                    <div className="flex items-center gap-3">
                                        {b.logo_url ? (
                                            <img src={b.logo_url} alt={b.name} className="h-10 w-10 rounded-full object-cover bg-white border dark:border-slate-600" />
                                        ) : (
                                            <div className="h-10 w-10 rounded-full bg-amber-100 dark:bg-slate-700 flex items-center justify-center text-amber-700 dark:text-amber-400 font-bold">
                                                {(b.name || '?')[0]}
                                            </div>
                                        )}
                                        <div>
                                            <p className="font-medium dark:text-white">{b.name}</p>
                                            {b.description && <p className="text-xs text-muted-foreground dark:text-slate-400 max-w-xs truncate">{b.description}</p>}
                                        </div>
                                    </div>
                                </td>
                                <td className="p-4">
                                    <div className="flex flex-col gap-1 max-w-md">
                                        {(b.links_display || []).length === 0 && (
                                            <span className="text-xs text-muted-foreground dark:text-slate-400 italic">Без връзки — показва се навсякъде</span>
                                        )}
                                        {(b.links_display || []).map(l => (
                                            <div key={l.category_id} className="flex flex-wrap items-center gap-1">
                                                <Badge variant="secondary" className="text-xs">{l.category_name}</Badge>
                                                {(l.subcategories || []).length > 0 ? (
                                                    l.subcategories.map(s => <Badge key={s} variant="outline" className="text-[10px] border-amber-300 text-amber-700 dark:text-amber-400">{s}</Badge>)
                                                ) : (
                                                    <span className="text-[10px] text-muted-foreground dark:text-slate-400">цялата категория</span>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </td>
                                <td className="p-4 dark:text-slate-300">{b.product_count}</td>
                                <td className="p-4">
                                    <Switch checked={b.is_active !== false} onCheckedChange={() => toggleActive(b)} data-testid={`brand-active-switch-${b.slug}`} />
                                </td>
                                <td className="p-4 text-right whitespace-nowrap">
                                    <Button variant="ghost" size="icon" onClick={() => openEdit(b)} className="dark:hover:bg-slate-700" data-testid={`edit-brand-${b.slug}`}><Edit2 className="h-4 w-4 dark:text-slate-300" /></Button>
                                    <Button variant="ghost" size="icon" onClick={() => tryDelete(b)} className="dark:hover:bg-slate-700" data-testid={`delete-brand-${b.slug}`}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Merge dialog */}
            <Dialog open={showMerge} onOpenChange={setShowMerge}>
                <DialogContent className="dark:bg-slate-800 dark:border-slate-700">
                    <DialogHeader><DialogTitle className="dark:text-white">Обедини дублирани марки</DialogTitle></DialogHeader>
                    <div className="space-y-4">
                        <div>
                            <Label className="dark:text-slate-300">Марка за премахване (източник)</Label>
                            <Select value={mergeSource} onValueChange={setMergeSource}>
                                <SelectTrigger className="dark:bg-slate-700 dark:border-slate-600" data-testid="merge-source-select"><SelectValue placeholder="Избери..." /></SelectTrigger>
                                <SelectContent className="dark:bg-slate-800">
                                    {brands.map(b => <SelectItem key={b.brand_id} value={b.brand_id}>{b.name} ({b.product_count})</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label className="dark:text-slate-300">Обедини в (целева марка)</Label>
                            <Select value={mergeTarget} onValueChange={setMergeTarget}>
                                <SelectTrigger className="dark:bg-slate-700 dark:border-slate-600" data-testid="merge-target-select"><SelectValue placeholder="Избери..." /></SelectTrigger>
                                <SelectContent className="dark:bg-slate-800">
                                    {brands.filter(b => b.brand_id !== mergeSource).map(b => <SelectItem key={b.brand_id} value={b.brand_id}>{b.name} ({b.product_count})</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                        <p className="text-xs text-muted-foreground dark:text-slate-400">Всички продукти от източника ще бъдат прехвърлени към целевата марка, а източникът ще бъде изтрит.</p>
                        <Button onClick={handleMerge} className="btn-primary w-full" data-testid="confirm-merge-btn">Обедини</Button>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Delete with linked products dialog */}
            <Dialog open={!!deleteInfo} onOpenChange={(open) => !open && setDeleteInfo(null)}>
                <DialogContent className="dark:bg-slate-800 dark:border-slate-700">
                    <DialogHeader><DialogTitle className="dark:text-white">Изтриване на марка</DialogTitle></DialogHeader>
                    {deleteInfo && (
                        <div className="space-y-4">
                            <p className="text-sm dark:text-slate-300">
                                Марката <strong>"{deleteInfo.brand.name}"</strong> има <strong>{deleteInfo.product_count}</strong> свързани продукта:
                            </p>
                            <div className="max-h-32 overflow-y-auto text-xs space-y-1 bg-slate-50 dark:bg-slate-700 p-2 rounded">
                                {(deleteInfo.products || []).map(p => <p key={p.product_id} className="dark:text-slate-300">• {p.name || p.product_id}</p>)}
                                {deleteInfo.product_count > (deleteInfo.products || []).length && <p className="dark:text-slate-400">... и още {deleteInfo.product_count - deleteInfo.products.length}</p>}
                            </div>
                            <div>
                                <Label className="dark:text-slate-300 text-sm">Прехвърли продуктите към друга марка (по избор)</Label>
                                <Select value={reassignTo} onValueChange={setReassignTo}>
                                    <SelectTrigger className="dark:bg-slate-700 dark:border-slate-600" data-testid="reassign-brand-select"><SelectValue placeholder="Без прехвърляне" /></SelectTrigger>
                                    <SelectContent className="dark:bg-slate-800">
                                        {brands.filter(b => b.brand_id !== deleteInfo.brand.brand_id).map(b => <SelectItem key={b.brand_id} value={b.brand_id}>{b.name}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="flex gap-2">
                                {reassignTo ? (
                                    <Button onClick={() => confirmDelete('reassign')} className="btn-primary flex-1" data-testid="confirm-delete-reassign-btn">Прехвърли и изтрий</Button>
                                ) : (
                                    <Button onClick={() => confirmDelete('force')} variant="destructive" className="flex-1" data-testid="confirm-delete-force-btn">Изтрий (продуктите остават без марка)</Button>
                                )}
                                <Button variant="outline" onClick={() => setDeleteInfo(null)} className="dark:border-slate-600 dark:text-white">Отказ</Button>
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default BrandsManager;
